import { Badge } from '../components/Badge';
import { Card, CardHeader } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { ThemeToggle } from '../components/ThemeToggle';
import { roleLabel } from '../auth/roles';
import { usePrincipal } from '../auth/usePrincipal';
import { TimezonePicker } from '../time/TimezonePicker';
import { useTheme } from '../theme/ThemeProvider';
import { useFormatters } from '../time/format';

/**
 * Personal preferences, all of them client-side.
 *
 * Nothing on this page is policy. Containment modes, protected principals and the approval
 * TTL live in `cdk.json` and changing one is a deploy — they are shown read-only on Health so
 * there is no impression that an operator can loosen a containment guard from a browser.
 */
export function Settings() {
  const principal = usePrincipal();
  const { choice, resolved } = useTheme();
  const { precise } = useFormatters();

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Display preferences for this browser, and who you are signed in as."
      />

      <div className="space-y-6">
        <Card>
          <CardHeader
            title="Timezone"
            subtitle="Applies to every timestamp in the dashboard"
          />
          <TimezonePicker />
        </Card>

        <Card>
          <CardHeader title="Appearance" subtitle="Dark, light, or follow this device" />
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggle />
            <p className="text-sm text-muted">
              {choice === 'system'
                ? `Following this device, currently ${resolved}.`
                : `Pinned to ${choice}.`}
            </p>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Signed in as"
            subtitle="From your Entra ID token. Roles are assigned on the API app registration."
          />
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Row label="Name" value={principal.name || '—'} />
            <Row label="Username" value={principal.upn || '—'} />
            <Row label="Object id" value={principal.subject || '—'} mono />
            <Row label="Tenant" value={principal.tenantId || '—'} mono />
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-subtle">Effective role:</span>
            <Badge tone="brand">{roleLabel(principal.role)}</Badge>
            {principal.rawRoles.map((role) => (
              <Badge key={role} tone="neutral">
                {role}
              </Badge>
            ))}
          </div>
          {principal.rawRoles.length === 0 ? (
            <p className="mt-2 text-xs text-subtle">
              Your token carries no app roles. Every API route will refuse — ask an
              administrator to assign one of IRA.Viewer, IRA.Analyst, IRA.Responder or
              IRA.Admin.
            </p>
          ) : null}
          <p className="mt-3 border-t border-line pt-3 text-xs text-subtle">
            The dashboard uses your role to decide what to show. It is not what permits
            anything — the API re-derives it from your token and enforces it on every request.
          </p>
        </Card>

        <Card>
          <CardHeader title="Current time" subtitle="As this dashboard will render it" />
          <p className="tabular-nums text-sm text-body">{precise(new Date().toISOString())}</p>
        </Card>
      </div>
    </>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line pb-1">
      <dt className="shrink-0 text-subtle">{label}</dt>
      <dd className={`truncate text-right text-body ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
